import {Environment, RecordSource, Store} from 'relay-runtime';
import {RelayNetworkLayer, urlMiddleware, loggerMiddleware, retryMiddleware} from 'react-relay-network-modern';
import * as tracking from './tracking';
import {hasFeatureFlag} from './forecast-app/shared/util/FeatureUtil';
import LogRocket from 'logrocket';
import DirectApi from './directApi';
import {Logger} from './Logger';
import {uploadProgressMiddleware} from './forecast-app/shared/util/uploadProgress';

const MAX_CONCURRENT_QUERIES = 6;
let activeQueries = 0;
const queryQueue = [];

const queueMiddleware = next => async req => {
	//Skip if the user does not have the feature flag
	if (!hasFeatureFlag('limit_concurrent_queries')) {
		return next(req);
	}
	return new Promise((resolve, reject) => {
		const executeQuery = async () => {
			//Only execute if the number of active queries is less than the max concurrent queries
			if (activeQueries < MAX_CONCURRENT_QUERIES) {
				activeQueries++;
				try {
					const result = await next(req);
					resolve(result);
				} catch (error) {
					reject(error);
				} finally {
					activeQueries--;
					// Process next queued query
					if (queryQueue.length > 0) {
						const nextQuery = queryQueue.shift();
						nextQuery();
					}
				}
			} else {
				// Queue the request
				queryQueue.push(() => {
					executeQuery();
				});
			}
		};

		executeQuery();
	});
};

const RelayEnvironment = (function () {
	let instance;
	let instanceMC;

	function createInstance(isMC) {
		// Another option for disabling the garbage collection
		const store = new Store(new RecordSource());
		store.holdGC(); // Disable GC on the store. (For newer versions)

		const network = new RelayNetworkLayer([
			// Custom middleware to add LogRocket sessionUrl as a header
			next => async req => {
				const logRocketSessionUrl = LogRocket.sessionURL;
				if (
					logRocketSessionUrl !== null &&
					typeof logRocketSessionUrl === 'string' &&
					logRocketSessionUrl.startsWith('http')
				) {
					req.fetchOpts.headers['X-LogRocket-URL'] = logRocketSessionUrl;
				}
				return await next(req);
			},
			// Queue middleware to limit concurrent queries
			queueMiddleware,
			// Custom middleware to handle relay file upload -> apollo-upload
			next => async req => {
				const {
					id,
					operation: {text: query},
					variables,
					uploadables,
				} = req;

				if (uploadables) {
					const graphQLMultipart = new FormData();

					const fileMap = [];

					const writeMapFromFileAndMarkVariable = (searchable, parents) => {
						Object.keys(searchable).forEach(key => {
							if (key === 'id') {
								return;
							}
							const currentValue = searchable[key];
							if (
								typeof currentValue === 'object' &&
								(currentValue.constructor === Object || currentValue.constructor === Array)
							) {
								// Recursive
								writeMapFromFileAndMarkVariable(currentValue, [...parents, key]);
							} else {
								// Consider the non-plain objects inside uplodables as File data.

								fileMap.push({
									operationPath: ['variables', ...parents, key].join('.'),
									file: currentValue,
								});

								// Synchronize variable with uploadables.

								let currentDepthVariable = variables;

								parents.forEach(parent => {
									if (!currentDepthVariable[parent]) {
										currentDepthVariable[parent] = {};
									}

									currentDepthVariable = currentDepthVariable[parent];
								});
							}
						});
					};
					writeMapFromFileAndMarkVariable(uploadables, []);

					const operations = {
						id,
						query,
						variables,
					};

					graphQLMultipart.append('operations', JSON.stringify(operations));
					graphQLMultipart.append(
						'map',
						JSON.stringify(
							fileMap.reduce((reducedMap, value, index) => ({...reducedMap, [index]: [value.operationPath]}), {})
						)
					);

					fileMap.forEach((mapValue, index) => {
						graphQLMultipart.append(index, mapValue.file);
					});

					req.fetchOpts.body = graphQLMultipart;
				}
				const res = await next(req);

				return res;
			},
			urlMiddleware({
				url: () => Promise.resolve(isMC ? process.env.GRAPHQL_ENDPOINT : DirectApi.graphqlServerEndpoint('graphql')),
				credentials: 'include',
			}),
			loggerMiddleware(),
			retryMiddleware({
				fetchTimeout: 120000,
				retryDelays: () => 1000,
				beforeRetry: ({forceRetry, abort, delay, attempt, lastError, req}) => {
					if (attempt > 4) abort(lastError);
					tracking.trackEvent('Relay Retry');
					window.forceRelayRetry = forceRetry;
					Logger.debug('call `forceRelayRetry()` for immediately retry! Or wait ' + delay + ' ms.');
				},
			}),
			// Sends an upload whose progress is being tracked with XMLHttpRequest; see uploadProgress.ts.
			uploadProgressMiddleware,
		]);

		return new Environment({
			network: network,
			store: store,
		});
	}

	return {
		getInstance: function () {
			if (!instance) {
				Logger.info('Creating RelayEnvironment instance');
				instance = createInstance(false);
			}
			return instance;
		},
		getInstanceMC: function () {
			if (!instanceMC) {
				Logger.info('Creating RelayEnvironment instance for MC');
				instanceMC = createInstance(true);
			}
			return instanceMC;
		},
	};
})();

export default RelayEnvironment;
